import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { refreshCloudfrontCookies } from '../../utils/axios';
import Swal from 'sweetalert2';
import { loginOtpInitiate, loginOtpSend, loginOtpVerify } from '../../utils/urls';
import { setIdleTimeoutSeconds, consumeLogoutMessage } from '../../utils/session';
import jwt_decode from "jwt-decode";
import { useDispatch } from 'react-redux';
import { setUser } from '../../utils/userSlice';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import FlexComponent from '../FlexComponent';
import ForgotPassword from './ForgotPassword';

function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [forgotPassword, setForgotPassword] = useState(false);
  const [step, setStep] = useState('credentials');
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");

  const [otpSessionId, setOtpSessionId] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [maskedValue, setMaskedValue] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const [loading, setLoading] = useState(false);
  const [loadingChannel, setLoadingChannel] = useState(null);
  const [error, setError] = useState("");

  // Resend Timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (localStorage.getItem("authTokens")) {
      navigate(localStorage.getItem("prevUrl") || "/");
    }
  }, [navigate]);

  // Explain why the user landed here if they were signed out (expiry /
  // revocation / idle) rather than logging out manually.
  useEffect(() => {
    const message = consumeLogoutMessage();
    if (message) {
      Swal.fire({ icon: "info", title: "Signed out", text: message });
    }
  }, []);


  // ==================== API Functions ====================
  const initiateOtp = async () => {
    if (!username || !password) {
      setError("Username and password are required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(loginOtpInitiate, { username, password });
      setOtpSessionId(res.data.otp_session_id);
      setChannels(res.data.channels || []);

      if (res.data.channels?.length === 1) {
        await sendOtp(res.data.channels[0], res.data.otp_session_id);
      } else {
        setStep('channel');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong";
      setError(detail);
      Swal.fire({ icon: "error", title: "Error", text: detail });
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (channel, sessionId = null) => {
    // setLoading(true);
    setLoadingChannel(channel.type)
    try {
      await axios.post(loginOtpSend, {
        otp_session_id: sessionId || otpSessionId,
        type: channel.type,
        channel_fingerprint: channel.channel_fingerprint
      });

      setSelectedChannel(channel);
      setMaskedValue(channel.masked_value);
      setStep('otp');
      setResendTimer(60);
      setOtp("");
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to send OTP" });
    } finally {
      setLoadingChannel(null);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      setError("Please enter 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // const response = await axios.post(loginOtpVerify, {
      //   otp_session_id: otpSessionId,
      //   otp: otp.trim()
      // });

      const response = await axios.post(
        loginOtpVerify,
        {
          otp_session_id: otpSessionId,
          otp: otp.trim()
        },
        {
          withCredentials: true
        }
      );

      const tokens = response.data;

      // const expiresIn = 216000
      // const expiresIn = 129600;
      const expiresIn = 86400;
      const expiryTime = Date.now() + (expiresIn * 1000);

      localStorage.setItem(
        'cloudfront_cookie_expiry',
        expiryTime.toString()
      );

      // Save Auth Data
      localStorage.setItem('authTokens', JSON.stringify(tokens));
      setIdleTimeoutSeconds(tokens.idle_timeout_seconds);
      localStorage.setItem('activePipeline', tokens.active_pipeline_account);
      localStorage.setItem('appPipeline', tokens.app_pipeline_account);
      localStorage.setItem('defaultLanguage', JSON.stringify(tokens.languages || []));

      // Save CloudFront Cookies
      // saveCloudFrontCookies(response);
      // await refreshCloudfrontCookies()

      const decoded = jwt_decode(tokens.access);

      dispatch(setUser({
        userId: decoded.user_id,
        isSuperuser: decoded.is_superuser,
        designation: decoded?.designation || "",
        activePipeline: tokens.active_pipeline_account,
        appPipeline: tokens.app_pipeline_account,
        defaultLanguage: tokens.languages || [],
        bizCardUrl: decoded.biz_card_url || null,
        locationCardUrl: decoded.location_card_url || null,
        languages: decoded.languages || [],
      }));

      Swal.fire({ icon: "success", title: "Login Successful", timer: 1500, showConfirmButton: false });
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid or expired OTP";
      setError(detail);
      Swal.fire({ icon: "error", title: "Error", text: detail });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 'credentials') initiateOtp();
    else if (step === 'otp') verifyOtp();
  };

  const handleResend = () => {
    if (resendTimer > 0 || !selectedChannel) return;
    sendOtp(selectedChannel);
  };

  const resetFlow = () => {
    setStep('credentials');
    setOtp("");
    setOtpSessionId(null);
    setChannels([]);
    setError("");
  };

  if (forgotPassword) {
    return <ForgotPassword onBackToLogin={() => setForgotPassword(false)} />;
  }

  return (
    <FlexComponent justifyContent="center">
      <form onSubmit={handleSubmit} style={{ zIndex: "1", width: "100%", maxWidth: "420px" }}>
        <p style={{ fontSize: '18px', color: "white", textAlign: "center" }}>
          Log in to breadcrumbs
        </p>

        {/* Credentials Step */}
        {step === 'credentials' && (
          <div style={{ marginTop: '1.5rem' }}>
            <InputText
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            <Password
              placeholder="Password"
              feedback={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%' }}
            />
            <div style={{ textAlign: 'right', marginTop: '0.6rem' }}>
              <span
                role="button"
                tabIndex={0}
                className="otp-link"
                onClick={() => { setError(""); setForgotPassword(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setError(""); setForgotPassword(true); } }}
                style={{ color: 'white', fontSize: '14px' }}
              >
                Forgot Password?
              </span>
            </div>
          </div>
        )}

        {/* Channel Selection Step */}
        {step === 'channel' && (
          <div style={{ marginTop: '1.5rem', color: 'white' }}>
            <h3 style={{ textAlign: 'center', marginBottom: "1rem" }}>Choose OTP Delivery Method</h3>
            {channels.map((ch, i) => (
              <Button
                key={i}
                onClick={() => sendOtp(ch)}
                style={{
                  padding: '1rem',
                  // border: '1px solid #555',
                  borderRadius: '8px',
                  margin: '20px 10px',
                  width: "100%",
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                loading={loadingChannel === ch.type}
                className='glassBox otpDeliveryOptions'
                label={`${ch.type.toUpperCase()} — ${ch.masked_value}`}
              />
            ))}
          </div>
        )}

        {/* OTP Step */}
        {step === 'otp' && (
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <p style={{ color: "white" }} >Enter OTP sent to</p>
            <p style={{ fontWeight: 'bold', marginBottom: '1rem', color: "white" }}>{maskedValue}</p>

            <InputText
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ width: '100%', fontSize: '1.8rem', textAlign: 'center', letterSpacing: '25px' }}
              maxLength={6}
            />

            <Button
              type="button"
              label={resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
              disabled={resendTimer > 0}
              className="p-button-text p-button-sm"
              onClick={handleResend}
              style={{ marginTop: '1rem', color: "white", border: resendTimer === 0 && "1px solid grey" }}
            />
          </div>
        )}

        {error && <p style={{ color: '#ff6b6b', textAlign: 'center', margin: '1rem 0' }}>{error}</p>}

        {(step === 'credentials' || step === 'otp') && (
          <Button
            type="submit"
            loading={loading}
            label={step === 'credentials' ? "Submit" : "Verify OTP"}
            style={{
              width: '100%',
              marginTop: '1.5rem',
              background: 'white',
              color: 'black',
              fontWeight: '500',
              padding: '12px'
            }}
          />
        )}

        {/* {step !== 'credentials' && (
          <Button
            type="button"
            label="← Back"
            className="p-button-outlined"
            onClick={resetFlow}
            style={{ width: '100%', marginTop: '0.8rem' }}
          />
        )} */}
      </form>
    </FlexComponent>
  );
}

export default LoginForm;